import { defineStore } from 'pinia'
import { ref, shallowRef, computed, watch } from 'vue'
import { useRPilotConfig } from '../composables/useRPilotConfig.js'
import { getSession } from '../api/sessions.js'
import { setAutoAccept } from '../api/chat.js'
import { useSessionsStore } from './sessions.js'

export const useChatStore = defineStore('chat', () => {
  const cfg = useRPilotConfig()
  const sessionsStore = useSessionsStore()
  const namespace = cfg?.namespace || 'rpilot'
  const showToolCallsKey = `${namespace}.show-tool-calls`

  const messages = ref([])
  const streamingContent = ref('')
  const streamingAnalysis = ref('')
  const isStreaming = ref(false)
  // AbortController of the in-flight SSE request. Lives here rather than in
  // useChat's per-instance state so a widget remounted mid-stream — e.g. a
  // host view switch triggered by a frontend tool, with persistSession
  // carrying the transcript over — can still stop the stream its predecessor
  // instance started. shallowRef: an AbortController must not be wrapped in a
  // reactive proxy (its methods reject proxied `this`).
  const abortController = shallowRef(null)
  // A frontend tool call surfaced by the SSE `frontend_tool_call` event. The
  // host answers it via @tool-call on RPilotChat (respond); until then the
  // turn is paused server-side and the widget reports itself busy (isBusy).
  const pendingFrontendToolCall = ref(null)
  // Host-supplied one-liner for the loader while a frontend tool is pending
  // (e.g. "Review the proposal, then Save or Reject"), set via the `setStatus`
  // callback on the @tool-call payload. Cleared with the pending call.
  const frontendToolStatus = ref('')
  // Cancels the pending frontend tool call on the host's behalf — installed by
  // ChatContainer alongside the respond closure so Stop (from any widget
  // instance, including one remounted mid-pause) can answer the call with a
  // cancel error and let the turn resume. shallowRef: plain function holder.
  const frontendToolCanceller = shallowRef(null)
  // The turn is in flight from the user's point of view: either an SSE
  // response is open, or the backend has paused the turn on a frontend tool
  // and is waiting for the host to answer it. The harness closes the /chat
  // stream right after `frontend_tool_call`, so `isStreaming` alone drops to
  // false while the host works (or waits on the user) — every "agent is
  // working" gate (loader, shimmer, composer lock, rating footer) keys off
  // this instead so the chat never looks idle mid-turn.
  const isBusy = computed(() => isStreaming.value || pendingFrontendToolCall.value !== null)
  // Docs pages emitted by rdrive_show_docs_page that are waiting to be
  // inserted after the agent's next final text response, so a lead-in like
  // "Here is the guide…" appears before the embedded viewer.
  const pendingDocsPages = ref([])

  // Skill selection. `activeSkillName` mirrors the server's session state via
  // the skill_activated / skill_deactivated SSE events (emitted for both the
  // composer dropdown and the model's own activation tool). `pendingSkillName`
  // is a dropdown pick that hasn't been sent yet — it rides on the next /chat
  // request as the one-shot `skillName` directive. `pendingDeactivate` is a
  // queued "No skill" pick, sent as the one-shot `deactivateSkill` directive;
  // mutually exclusive with `pendingSkillName`.
  const activeSkillName = ref(null)
  const pendingSkillName = ref(null)
  const pendingDeactivate = ref(false)

  // Set when a turn is rejected for credit reasons (SSE `error` with
  // code no_pool | insufficient_credits). Renders a calm inline notice instead
  // of a raw error bubble (see addCreditExhausted) and disables the composer so
  // the user can't keep firing off refused turns.
  const creditExhausted = ref(false)

  // Governs the tool-activity timeline only (tool calls + intermediate
  // _thinking content). Reasoning text is always available and gated
  // separately by the `features.thinking` prop at the component layer.
  const showToolCalls = ref(
    safeReadStorage(showToolCallsKey) === 'true'
  )

  function toggleToolCalls() {
    showToolCalls.value = !showToolCalls.value
    safeWriteStorage(showToolCallsKey, showToolCalls.value)
  }

  // Session-scoped "auto-accept edits" flag. When on, the backend skips the
  // human-in-the-loop approval prompt for mutating tools. The server keeps this
  // per session and does NOT persist it, so we mirror that: reset to false
  // whenever the active session changes (below).
  const autoAcceptMutations = ref(false)

  async function toggleAutoAccept() {
    const enabled = !autoAcceptMutations.value
    autoAcceptMutations.value = enabled // optimistic
    const sessionId = sessionsStore.activeSessionId
    if (!sessionId) return
    try {
      await setAutoAccept({ sessionId, enabled })
    } catch {
      autoAcceptMutations.value = !enabled // revert on failure
    }
  }

  // The backend's auto-accept flag lives per session and isn't persisted, so
  // drop our mirror the moment the active session changes (switch, new chat).
  watch(() => sessionsStore.activeSessionId, () => {
    autoAcceptMutations.value = false
  })

  const allMessages = computed(() => {
    let result = [...messages.value]

    if (streamingAnalysis.value) {
      result.push({
        role: 'assistant',
        content: streamingAnalysis.value,
        _analysis: true,
        _streaming: true,
      })
    }

    if (streamingContent.value) {
      result.push({
        role: 'assistant',
        content: streamingContent.value,
        _streaming: true,
      })
    }

    // The toggle governs only the tool-activity timeline. Reasoning
    // (_analysis) is always kept here; whether it renders is decided by the
    // features.thinking gate at the component layer. Note `tool_approval` and
    // `tool_approval_batch` messages are deliberately NOT filtered: an approval
    // request is a blocking prompt the user must answer, so it must render
    // regardless of the show-tool-calls setting (the predicate below only drops
    // `_thinking` and `tool_call` rows).
    if (!showToolCalls.value) {
      result = result.filter(m => !m._thinking && m.role !== 'tool_call')
    }

    return result
  })

  /**
   * Group consecutive thinking items into blocks. Reasoning (_analysis, fed by
   * both the `analysis` and `thinking` SSE events) groups into a
   * `_reasoningBlock`; tool activity (tool_call + intermediate _thinking
   * content) groups into a `_thinkingBlock`. A run switches blocks when its
   * kind changes, so an interleaved think → tool → think turn renders as
   * separate ordered blocks.
   */
  const groupedMessages = computed(() => {
    const src = allMessages.value
    const result = []
    let currentBlock = null

    const flush = () => {
      if (currentBlock) { result.push(currentBlock); currentBlock = null }
    }

    for (const msg of src) {
      let kind = null
      if (msg._analysis) kind = 'reasoning'
      else if (msg._thinking || msg.role === 'tool_call') kind = 'tool'

      if (kind) {
        if (!currentBlock || currentBlock.kind !== kind) {
          flush()
          currentBlock = kind === 'reasoning'
            ? { kind, _reasoningBlock: true, items: [] }
            : { kind, _thinkingBlock: true, items: [] }
        }
        currentBlock.items.push(msg)
      } else {
        flush()
        result.push(msg)
      }
    }
    flush()
    return result
  })

  // Model-written summary of the tool call the turn is paused on, for the
  // loader's status line while the host handles a frontend tool. '' when the
  // call carried no summary (the loader then shows a generic waiting label).
  const pendingFrontendToolSummary = computed(() => {
    const pending = pendingFrontendToolCall.value
    if (!pending) return ''
    const tc = messages.value.find(
      m => m.role === 'tool_call' && m.toolCallId === pending.toolCallId
    )
    return tc?.summary ? String(tc.summary).trim() : ''
  })

  // Single-line "what's it doing" status for the agent loader (see
  // ChatContainer.spinnerStatus, which handles the frontend-tool pause before
  // consulting this). In priority order: the
  // actively streaming reasoning, then the most recent activity in the current
  // turn — a tool call's model-written summary or a line of reasoning,
  // whichever is later. `includeReasoning` is false when features.thinking is
  // off, so reasoning text isn't surfaced while the reasoning panel is hidden;
  // tool summaries (explicit, user-facing descriptions) always show. Returns ''
  // when there's nothing to show, leaving the loader's default "Thinking…".
  function latestStatusLine(includeReasoning = true) {
    if (includeReasoning) {
      const streaming = lastLine(streamingAnalysis.value)
      if (streaming) return streaming
    }
    for (let i = messages.value.length - 1; i >= 0; i--) {
      const m = messages.value[i]
      if (m.role === 'user') break
      if (m.role === 'tool_call' && m.summary) return String(m.summary).trim()
      if (includeReasoning && (m._thinking || m._analysis)) {
        const line = lastLine(m.content)
        if (line) return line
      }
    }
    return ''
  }

  /** Queue a dropdown pick for the next message. Re-picking the queued skill
   *  un-queues it; picking the already-active skill is a no-op. */
  function selectSkill(name) {
    pendingDeactivate.value = false
    if (name === activeSkillName.value || name === pendingSkillName.value) {
      pendingSkillName.value = null
      return
    }
    pendingSkillName.value = name
  }

  /** "No skill" pick. Un-queues a pending skill pick if there is one;
   *  otherwise toggles a pending deactivation of the server-active skill. */
  function selectNoSkill() {
    if (pendingSkillName.value) {
      pendingSkillName.value = null
      return
    }
    if (pendingDeactivate.value) {
      pendingDeactivate.value = false
      return
    }
    if (activeSkillName.value) pendingDeactivate.value = true
  }

  /** Consume the queued skill directive — sent on exactly one /chat request. */
  function takePendingSkillDirective() {
    const directive = {
      skillName: pendingSkillName.value,
      deactivate: pendingDeactivate.value,
    }
    pendingSkillName.value = null
    pendingDeactivate.value = false
    return directive
  }

  function setSkillActivated(name) {
    activeSkillName.value = name || null
  }

  function setSkillDeactivated() {
    activeSkillName.value = null
    pendingDeactivate.value = false
  }

  function clear() {
    messages.value = []
    streamingContent.value = ''
    streamingAnalysis.value = ''
    isStreaming.value = false
    pendingFrontendToolCall.value = null
    frontendToolStatus.value = ''
    frontendToolCanceller.value = null
    pendingDocsPages.value = []
    activeSkillName.value = null
    pendingSkillName.value = null
    pendingDeactivate.value = false
    creditExhausted.value = false
  }

  // Move queued docs_page entries into the transcript.
  function flushPendingDocsPages() {
    if (pendingDocsPages.value.length === 0) return
    for (const p of pendingDocsPages.value) messages.value.push(p)
    pendingDocsPages.value = []
  }

  /** Replace the transcript with messages from an existing session. */
  async function loadSession(sessionId) {
    clear()
    if (!sessionId) return
    const session = await getSession(sessionId)
    messages.value = transformMessages(session.messages || [])
    // Best-effort: the in-memory session DTO carries activeSkillName; the
    // SQL-audit detail shape doesn't (yet). Server-side skill state survives
    // either way — this only seeds the indicator.
    activeSkillName.value = session.activeSkillName || null
  }

  function addUserMessage(content) {
    // Flush any leftover docs pages from the previous turn before the new
    // user message so they don't get reordered into the new turn.
    flushPendingDocsPages()
    messages.value.push({ role: 'user', content })
  }

  function appendAnalysis(token) {
    streamingAnalysis.value += token
  }

  function finalizeAnalysis() {
    if (streamingAnalysis.value) {
      messages.value.push({
        role: 'assistant',
        content: streamingAnalysis.value,
        _analysis: true,
      })
      streamingAnalysis.value = ''
    }
  }

  function appendContent(token) {
    finalizeAnalysis()
    streamingContent.value += token
  }

  function finalizeContent(reason = 'done') {
    if (reason === 'done' && streamingAnalysis.value && !streamingContent.value) {
      streamingContent.value = streamingAnalysis.value
      streamingAnalysis.value = ''
    } else {
      finalizeAnalysis()
    }
    if (streamingContent.value) {
      const msg = { role: 'assistant', content: streamingContent.value }
      if (reason === 'tool_call') msg._thinking = true
      messages.value.push(msg)
      streamingContent.value = ''
    }
    // Flush docs pages after the assistant text is in place, so the lead-in
    // appears above the embedded viewer.
    if (reason === 'done') flushPendingDocsPages()
  }

  function addToolCall({ toolCallId, name, arguments: args, summary }) {
    finalizeAnalysis()
    finalizeContent('tool_call')
    const parsedArgs = tryParse(args)
    messages.value.push({
      role: 'tool_call',
      toolCallId,
      name,
      // One-line intent the model wrote for this call (e.g. "Searching issues
      // for water damage"), forwarded on the SSE event. Falls back to the raw
      // `_summary` argument, then null so the UI shows the tool name instead.
      summary: summary || readSummary(parsedArgs),
      arguments: stripSummary(parsedArgs),
      argumentsRaw: args,
      result: null,
      error: null,
      status: 'running',
    })
  }

  // Human-in-the-loop batch approval envelope. When 2+ mutating calls gate in a
  // single assistant round, the backend emits one `tool_approval_batch` event
  // *before* the per-call `tool_approval_required` events, so the UI can fold
  // them into one card instead of N. Those per-call events carry a matching
  // `batchId`; addPendingApproval routes them into this row's `items` array.
  // Mirrors addPendingApproval's finalize-then-push so the card lands after any
  // in-flight reasoning/content.
  function addPendingApprovalBatch({ batchId, count, headline, toolCounts }) {
    finalizeAnalysis()
    finalizeContent('tool_call')
    messages.value.push({
      role: 'tool_approval_batch',
      batchId,
      headline,
      toolCounts: toolCounts || [],
      expectedCount: count,
      items: [],
      status: 'pending',
    })
  }

  // Human-in-the-loop tool approval. The backend emits `tool_approval_required`
  // for a mutating tool and holds the SSE stream open until the user decides.
  // We model each request as its own transcript message (mirroring tool_call)
  // so it renders inline and — being a distinct role — survives the
  // show-tool-calls filter (see allMessages).
  function addPendingApproval({ toolCallId, name, description, summary, arguments: args, fieldTitles, batchId }) {
    const parsedArgs = tryParse(args)
    const item = {
      role: 'tool_approval',
      toolCallId,
      name,
      description,
      summary,
      arguments: stripSummary(parsedArgs),
      // versionReference → human title map from the backend, so the card can
      // label field writes by title instead of opaque reference.
      fieldTitles: fieldTitles || null,
      status: 'pending',
    }

    // Batched round: fold this item into the open envelope with a matching
    // batchId (the batch card already did the finalize/push). Falls through to a
    // standalone card when there's no live envelope — old backends, single-gate
    // rounds, or a defensive miss (envelope never arrived).
    if (batchId) {
      const batch = messages.value.find(
        m => m.role === 'tool_approval_batch' && m.batchId === batchId && m.status === 'pending'
      )
      if (batch) {
        batch.items.push(item)
        return
      }
    }

    finalizeAnalysis()
    finalizeContent('tool_call')
    messages.value.push(item)
  }

  // Settle an approval. `decision` is 'approved' | 'denied' | 'timeout' (or a
  // future value like 'auto_approved', treated as settled) — from either the
  // server's `tool_approval_resolved` event or the optimistic local update from
  // the card buttons (the server event overwrites it, harmlessly, since they
  // agree). Searches flat rows first, then items folded inside a batch card; a
  // batch row settles once every one of its items is no longer pending (the
  // card derives approved/denied counts from the items).
  function resolveApproval(toolCallId, decision) {
    const flat = messages.value.find(
      m => m.role === 'tool_approval' && m.toolCallId === toolCallId
    )
    if (flat) { flat.status = decision; return }

    for (const m of messages.value) {
      if (m.role !== 'tool_approval_batch') continue
      const item = m.items.find(it => it.toolCallId === toolCallId)
      if (!item) continue
      item.status = decision
      // Only aggregate to 'settled' once the whole round has arrived — an early
      // out-of-band decision on the first item mustn't collapse the card while
      // later per-call events are still streaming in.
      if (m.items.length >= (m.expectedCount || 0) && m.items.every(it => it.status !== 'pending')) {
        m.status = 'settled'
      }
      return
    }
  }

  // Roll a batch card back to pending after a failed bulk submit so its buttons
  // re-enable for a retry. Only the ids we optimistically settled are reset;
  // items settled out-of-band (excluded from the submit) stay put. The gates are
  // still live server-side — nothing actually resolved.
  function resetBatchApproval(batchId, toolCallIds) {
    const batch = messages.value.find(
      m => m.role === 'tool_approval_batch' && m.batchId === batchId
    )
    if (!batch) return
    const ids = new Set(toolCallIds)
    for (const it of batch.items) {
      if (ids.has(it.toolCallId)) it.status = 'pending'
    }
    batch.status = 'pending'
  }

  function setSyncProgress({ toolCallId, phase, source, fetched, total, message }) {
    const tc = messages.value.find(
      m => m.role === 'tool_call' && m.toolCallId === toolCallId
    )
    if (tc) {
      tc.syncProgress = { phase, source, fetched, total, message }
    }
  }

  function setToolResult({ toolCallId, name, result }) {
    const tc = messages.value.find(
      m => m.role === 'tool_call' && m.toolCallId === toolCallId
    )
    if (tc) {
      tc.result = result
      tc.status = 'success'
      tc.syncProgress = null
    }

    // Promote rdrive_show_docs_page results to a visible docs_page message,
    // deferred until after the agent's next final text response (see
    // flushPendingDocsPages in finalizeContent).
    const toolName = name || tc?.name
    if (toolName === 'rdrive_show_docs_page' && result) {
      try {
        const parsed = typeof result === 'string' ? JSON.parse(result) : result
        if (parsed && parsed.action === 'show_docs_page' && parsed.page) {
          pendingDocsPages.value.push({
            role: 'docs_page',
            page: parsed.page,
            url: parsed.url || '',
            content: parsed.content || '',
          })
        }
      } catch { /* ignore parse errors */ }
    }
  }

  function setToolError({ toolCallId, error }) {
    const tc = messages.value.find(
      m => m.role === 'tool_call' && m.toolCallId === toolCallId
    )
    if (tc) {
      tc.error = error
      tc.status = 'error'
    }
  }

  function setPendingFrontendToolCall({ toolCallId, name, arguments: argsRaw }) {
    // The round's tool_call event just demoted any streamed text to _thinking
    // (finalizeContent('tool_call')), which hides it in the tool timeline. But
    // text the model writes before pausing on a frontend tool is user-facing —
    // e.g. the proposal summary before rdrive_propose_schedule_commit — and
    // the turn stays paused until the user acts, so promote it back to a
    // normal bubble. Only the message directly behind this round's tool_call
    // rows is promoted; earlier intermediate text stays in the timeline.
    for (let i = messages.value.length - 1; i >= 0; i--) {
      const m = messages.value[i]
      if (m.role === 'tool_call') continue
      if (m.role === 'assistant' && m._thinking && !m._analysis) delete m._thinking
      break
    }
    let args = {}
    if (typeof argsRaw === 'string' && argsRaw.length > 0) {
      try { args = JSON.parse(argsRaw) } catch { args = {} }
    } else if (argsRaw && typeof argsRaw === 'object') {
      args = argsRaw
    }
    frontendToolStatus.value = ''
    pendingFrontendToolCall.value = { toolCallId, name, args }
  }

  function clearPendingFrontendToolCall() {
    pendingFrontendToolCall.value = null
    frontendToolStatus.value = ''
    frontendToolCanceller.value = null
  }

  /** Host-supplied loader text while a frontend tool is pending; ignored
   *  (and never sticky) once the call has been answered. */
  function setFrontendToolStatus(text) {
    if (!pendingFrontendToolCall.value) return
    frontendToolStatus.value = typeof text === 'string' ? text.trim() : ''
  }

  function addError(errorMsg) {
    messages.value.push({ role: 'assistant', content: `**Error:** ${errorMsg}`, isError: true })
  }

  // Credit-rejection turn: append a dedicated notice card (rendered by
  // CreditExhaustedNotice) rather than a generic error bubble, and latch the
  // composer-disabling flag. Idempotent — a turn emits at most one such error,
  // but guard against a duplicate card if called twice.
  function addCreditExhausted() {
    if (!creditExhausted.value) {
      messages.value.push({ role: 'credit_exhausted' })
    }
    creditExhausted.value = true
  }

  function appendLink({ url, markdown, viewType, displayName, description }) {
    finalizeContent('done')

    for (let i = messages.value.length - 1; i >= 0; i--) {
      const msg = messages.value[i]
      if (msg.role === 'assistant' && !msg._thinking && !msg._analysis) {
        // One turn can carry several link events (e.g. a file download chip AND a
        // navigation chip) — collect them all, deduping by url.
        msg._links = msg._links || []
        if (!msg._links.some((l) => l.url === url)) {
          msg._links.push({ url, markdown, viewType, displayName, description })
        }
        return
      }
    }

    messages.value.push({ role: 'assistant', content: markdown || `[${displayName}](${url})` })
  }

  return {
    messages, streamingContent, streamingAnalysis, isStreaming, isBusy, abortController,
    pendingFrontendToolCall, pendingFrontendToolSummary,
    frontendToolStatus, setFrontendToolStatus, frontendToolCanceller,
    activeSkillName, pendingSkillName, pendingDeactivate,
    selectSkill, selectNoSkill, takePendingSkillDirective, setSkillActivated, setSkillDeactivated,
    showToolCalls, toggleToolCalls,
    autoAcceptMutations, toggleAutoAccept,
    allMessages, groupedMessages, latestStatusLine,
    clear, loadSession, addUserMessage,
    appendAnalysis, finalizeAnalysis, appendContent, finalizeContent,
    addToolCall, setSyncProgress, setToolResult, setToolError,
    addPendingApprovalBatch, addPendingApproval, resolveApproval, resetBatchApproval,
    setPendingFrontendToolCall, clearPendingFrontendToolCall,
    addError, appendLink,
    creditExhausted, addCreditExhausted,
  }
})

// ToolName the backend stamps on the system audit row it writes whenever it
// emitted a `type: "link"` SSE event (see ChatEndpoints.LinkEventMarkerToolName).
// transformMessages keys off it to rebuild the navigation button on reload — the
// live stream's appendLink never runs for a re-opened session.
const LINK_EVENT_MARKER = 'link_event'

// Ported verbatim from the server's LinkDetectionRegex (ChatEndpoints.cs). Used
// to strip the raw markdown link / bare URL out of a reloaded assistant bubble
// once its link is re-rendered as a button — mirroring how the live stream strips
// URL lines once a link tool ran. Matches markdown links (balanced parens in the
// URL), bare http(s):// URLs, and localhost refs; it can NOT match [[rdrive:...]]
// inline tokens (they carry no URL), so those pass through untouched.
const LINK_DETECTION_REGEX =
  /\[.*?\]\(https?:\/\/(?:[^\s()]|\([^\s()]*\))*\)|https?:\/\/\S+|localhost\S*/gi

// Mirrors LINK_DETECTION_REGEX but for the upload metadata stub the SPA appends
// to a user message (for the model). A stub block is a line beginning `[File: `
// and the non-blank lines under it, up to a blank line or the end of the text;
// multiple attachments append multiple blocks. Display-only: the stub stays in
// what is SENT to the backend, but the user bubble strips it and renders an
// attachment chip instead (parseFileStubs). Kept in sync with the server's stub
// format ("[File: <name>, <size>, <n> sheets]" first line).
export const FILE_STUB_REGEX = /\[File: [^\]\n]*\][\s\S]*?(?=\n[ \t]*\n|$)/g

// Path shape of an authenticated download URL emitted by rdrive_export_file
// (Phase 5). Used on reload to re-hydrate a raw download link in replayed
// assistant content into a file_download chip, mirroring how LINK_DETECTION_REGEX
// re-hydrates deep links.
const DOWNLOAD_PATH_REGEX = /\/api\/files\/[A-Za-z0-9_-]+\/download/
// Strips the download link (markdown form or bare URL) from replayed assistant
// content once it's re-rendered as a chip — the download path may be relative
// (`/api/...`), which LINK_DETECTION_REGEX (http-only) wouldn't catch. The URL
// carries an authenticating `?sessionId=…` query the endpoint requires (Phase 5),
// so both branches tolerate a trailing query string.
const DOWNLOAD_LINK_STRIP_REGEX =
  /\[[^\]]*\]\(\S*\/api\/files\/[A-Za-z0-9_-]+\/download(?:\?[^)\s]*)?\)|\S*\/api\/files\/[A-Za-z0-9_-]+\/download(?:\?[^\s)]*)?/g

// A line left holding only its list marker after link stripping (e.g. the "- " that
// remains of "- [label](url)"). A lone "-" directly under a paragraph is a valid
// setext-heading underline, so marked would promote the paragraph to a giant <h2>.
// Blank such lines but keep their newlines so block separation survives. A "---"
// horizontal rule has more than one marker character and is not matched.
const ORPHAN_LIST_MARKER_LINE_REGEX = /^[ \t]*(?:[-*+]|\d{1,3}[.)])[ \t]*$/gm

// Shared cleanup for reloaded assistant content once its links re-render as chips:
// remove deep/finder links, download links, and any bullet the removal orphaned.
function stripLinkMarkup(content) {
  return content
    .replace(LINK_DETECTION_REGEX, '')
    .replace(DOWNLOAD_LINK_STRIP_REGEX, '')
    .replace(ORPHAN_LIST_MARKER_LINE_REGEX, '')
    .trim()
}

/**
 * Split a user-message text into its display text (stub blocks removed) and the
 * parsed attachments. Each attachment is read from a stub block's first line:
 * `[File: budget.xlsx, 4.2 MB, 3 sheets]` → { fileName, size, sheets }. Used by
 * MessageBubble for both live sends and reloaded sessions, so the raw stub never
 * shows to the user. Non-string / stub-free content passes through untouched.
 */
export function parseFileStubs(content) {
  if (typeof content !== 'string' || !content) return { text: content, attachments: [] }
  const attachments = []
  FILE_STUB_REGEX.lastIndex = 0
  let m
  while ((m = FILE_STUB_REGEX.exec(content)) !== null) {
    const firstLine = m[0].split('\n')[0]
    const inner = firstLine.replace(/^\[File:\s*/, '').replace(/\]\s*$/, '')
    const parts = inner.split(',').map((s) => s.trim())
    attachments.push({ fileName: parts[0] || '', size: parts[1] || '', sheets: parts[2] || '' })
  }
  const text = content.replace(FILE_STUB_REGEX, '').replace(/\n{3,}/g, '\n\n').trim()
  return { text, attachments }
}

// Pull a download link out of a raw assistant message on reload. Prefers a
// markdown link (so the label is the filename the model wrote); falls back to a
// bare URL. Returns { url, displayName } or null.
function detectDownloadLink(content) {
  if (typeof content !== 'string' || !DOWNLOAD_PATH_REGEX.test(content)) return null
  // The URL keeps its authenticating `?sessionId=…` query (Phase 5) — capture it so
  // the re-hydrated chip fetches the exact path the download endpoint accepts.
  const md = content.match(/\[([^\]]+)\]\((\S*\/api\/files\/[A-Za-z0-9_-]+\/download(?:\?[^)\s]*)?)\)/)
  if (md) return { displayName: md[1], url: md[2] }
  const bare = content.match(/(\S*\/api\/files\/[A-Za-z0-9_-]+\/download(?:\?[^\s)]*)?)/)
  if (bare) return { displayName: '', url: bare[1] }
  return null
}

/**
 * Reshape persisted session messages from the backend into the same shape the
 * streaming layer produces, so a resumed session renders identically to a live
 * one. Handles two backend shapes: flat (assistant rows that carry tool-call
 * fields directly) and nested OpenAI-style (tool_calls array on the assistant
 * message).
 */
function transformMessages(apiMessages) {
  const result = []
  const toolCallMap = new Map()
  // Defer docs_page entries until after the next final assistant message
  // so the lead-in text appears above the embedded viewer on resume.
  const pendingDocs = []
  function pushEntry(entry) {
    result.push(entry)
    if (entry.role === 'assistant' && !entry._thinking && !entry._analysis) {
      while (pendingDocs.length) result.push(pendingDocs.shift())
    }
  }

  // Re-render a persisted link-event payload as a navigation button, mirroring the
  // live `appendLink` action: hang it on the most recent real assistant bubble, or
  // fall back to a standalone bubble when there's no assistant text to attach to.
  function attachLink({ url, markdown, viewType, displayName, description }) {
    for (let i = result.length - 1; i >= 0; i--) {
      const entry = result[i]
      if (entry.role === 'assistant' && !entry._thinking && !entry._analysis) {
        // The audit row preserves the assistant's raw markdown link, but the live
        // stream strips URL lines once a link tool ran so the link shows ONLY as the
        // button. Strip here too, else the reloaded bubble double-renders it (inline
        // link + button). Keep the stripped result even if it's now empty — a
        // button-only bubble matches live behavior. [[rdrive:...]] inline tokens
        // carry no URL, so the regex leaves them untouched.
        if (typeof entry.content === 'string') {
          // Global regex — reset lastIndex so the .test() guard is state-independent.
          LINK_DETECTION_REGEX.lastIndex = 0
          if (LINK_DETECTION_REGEX.test(entry.content)) {
            entry.content = stripLinkMarkup(entry.content)
          }
        }
        // Multiple marker rows per turn are legitimate (download + navigation) —
        // append, deduping by url (the inline download-fallback below may have
        // already attached the same link before this marker row arrived).
        entry._links = entry._links || []
        if (!entry._links.some((l) => l.url === url)) {
          entry._links.push({ url, markdown, viewType, displayName, description })
        }
        return
      }
    }
    // No assistant bubble to attach to — surface the link as its own bubble. Use
    // pushEntry (not result.push) so any deferred docs pages flush in order.
    pushEntry({ role: 'assistant', content: markdown || `[${displayName}](${url})` })
  }

  for (const msg of apiMessages) {
    // Link-event marker: a system row the backend persists whenever it emitted a
    // `type: "link"` SSE event (both the nudge flow and the organic tool flow). It
    // carries the same payload the live stream received, in toolArguments (a JSON
    // string). Rebuild the button from it — this MUST run before the system-skip
    // below, since it is itself a system row. A malformed/legacy marker (parse
    // fails, or no url) is silently skipped so reload never crashes.
    if (msg.role === 'system' && msg.toolName === LINK_EVENT_MARKER) {
      const payload = tryParse(msg.toolArguments)
      if (payload && typeof payload === 'object' && payload.url) {
        attachLink(payload)
      }
      continue
    }

    if (msg.role === 'system') continue

    if (msg.role === 'user') {
      while (pendingDocs.length) result.push(pendingDocs.shift())
      result.push({ role: 'user', content: msg.content })
    } else if (msg.role === 'assistant') {
      if (msg.toolCallId && msg.toolName) {
        const entry = {
          role: 'tool_call',
          toolCallId: msg.toolCallId,
          name: msg.toolName,
          summary: readSummary(msg.toolArguments),
          arguments: stripSummary(tryParse(msg.toolArguments)),
          argumentsRaw: msg.toolArguments,
          result: null,
          error: null,
          status: 'success',
        }
        pushEntry(entry)
        toolCallMap.set(msg.toolCallId, entry)
      } else {
        if (msg.analysis) {
          pushEntry({ role: 'assistant', content: msg.analysis, _analysis: true })
        }
        if (msg.content) {
          const entry = { role: 'assistant', content: msg.content }
          if (msg.tool_calls && msg.tool_calls.length > 0) entry._thinking = true
          else {
            // A download URL the model wrote inline (a raw fallback — the normal
            // path is the persisted link_event marker handled by attachLink). Strip
            // it and render a file_download chip so reloaded sessions match live.
            const dl = detectDownloadLink(msg.content)
            if (dl) {
              entry.content = entry.content.replace(DOWNLOAD_LINK_STRIP_REGEX, '')
                .replace(ORPHAN_LIST_MARKER_LINE_REGEX, '').trim()
              entry._links = [{ url: dl.url, viewType: 'file_download', displayName: dl.displayName }]
            }
          }
          pushEntry(entry)
        }
        if (msg.tool_calls) {
          for (const tc of msg.tool_calls) {
            const entry = {
              role: 'tool_call',
              toolCallId: tc.id,
              name: tc.function.name,
              summary: readSummary(tc.function.arguments),
              arguments: stripSummary(tryParse(tc.function.arguments)),
              argumentsRaw: tc.function.arguments,
              result: null,
              error: null,
              status: 'success',
            }
            pushEntry(entry)
            toolCallMap.set(tc.id, entry)
          }
        }
      }
    } else if (msg.role === 'tool') {
      const tcId = msg.toolCallId || msg.tool_call_id
      const entry = toolCallMap.get(tcId)
      if (entry) {
        if (msg.content && typeof msg.content === 'string' && msg.content.startsWith('Error: ')) {
          entry.error = msg.content
          entry.status = 'error'
        } else {
          entry.result = msg.content

          // Mirror setToolResult: queue docs_page until the next final
          // assistant message in the transcript.
          if (entry.name === 'rdrive_show_docs_page' && msg.content) {
            try {
              const parsed = typeof msg.content === 'string' ? JSON.parse(msg.content) : msg.content
              if (parsed && parsed.action === 'show_docs_page' && parsed.page) {
                pendingDocs.push({
                  role: 'docs_page',
                  page: parsed.page,
                  url: parsed.url || '',
                  content: parsed.content || '',
                })
              }
            } catch { /* ignore parse errors */ }
          }
        }
      }
    }
  }
  // Flush any docs pages left after the last message.
  while (pendingDocs.length) result.push(pendingDocs.shift())

  return result
}

function tryParse(json) {
  if (typeof json !== 'string') return json
  try { return JSON.parse(json) } catch { return json }
}

// Last non-empty line of a multi-line string, trimmed. Lets the spinner status
// advance as reasoning streams in.
function lastLine(text) {
  const lines = (text || '').trim().split('\n').map(l => l.trim()).filter(Boolean)
  return lines.length ? lines[lines.length - 1] : ''
}

// The backend injects a synthetic `_summary` into every tool's arguments — a
// model-written one-liner describing the call's intent — and also forwards it
// on the tool_call SSE event as `summary`. It isn't a real tool parameter, so
// read it as the call's summary and strip it from the arguments we display.
// Accepts either a raw JSON string or an already-parsed object.
function readSummary(args) {
  const parsed = tryParse(args)
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && parsed._summary) {
    return String(parsed._summary)
  }
  return null
}

function stripSummary(args) {
  if (args && typeof args === 'object' && !Array.isArray(args) && '_summary' in args) {
    const { _summary, ...rest } = args
    return rest
  }
  return args
}

function safeReadStorage(key) {
  try { return window.localStorage.getItem(key) } catch { return null }
}

function safeWriteStorage(key, value) {
  try { window.localStorage.setItem(key, value) } catch { /* sandboxed iframe */ }
}
